import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Menu, X, Phone, Mail, Clock, MessageCircle, ChevronRight, Zap,
} from 'lucide-react';
import { CONTACT_EMAIL, SUPPORT_WHATSAPP_NUMBER, SUPPORT_WHATSAPP_URL } from '../../config';
import { useAuth } from '../../contexts/AuthContext';
import ThemeToggle from '../ThemeToggle';
import appIcon from '../../assets/brand/app-icon.png';

const SUPPORT_TEL = `+${SUPPORT_WHATSAPP_NUMBER}`;
const SUPPORT_DISPLAY = '+265 994 123 079';

const NAV = [
  { to: '/welcome', label: 'Home', end: true },
  { to: '/services', label: 'Services' },
  { to: '/about', label: 'About' },
  { to: '/help', label: 'Help' },
  { to: '/contact', label: 'Contact' },
];

export default function MarketingLayout({ children }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const primaryHref = user ? '/buy' : '/register';
  const primaryLabel = user ? 'Pay a bill' : 'Get started free';

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="hidden md:block bg-[#0F2E2B] text-[#99E1D9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-10 flex items-center justify-between text-xs sm:text-sm">
          <div className="flex items-center gap-5">
            <a href={`tel:${SUPPORT_TEL}`} className="inline-flex items-center gap-1.5 hover:text-white transition-colors">
              <Phone className="w-3.5 h-3.5" /> {SUPPORT_DISPLAY}
            </a>
            <a href={`mailto:${CONTACT_EMAIL}`} className="hidden lg:inline-flex items-center gap-1.5 hover:text-white transition-colors">
              <Mail className="w-3.5 h-3.5" /> {CONTACT_EMAIL}
            </a>
            <span className="hidden xl:inline-flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Help anytime — 24/7
            </span>
          </div>
          <a
            href={SUPPORT_WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 font-medium hover:text-white transition-colors"
          >
            <MessageCircle className="w-3.5 h-3.5" /> Chat on WhatsApp
          </a>
        </div>
      </div>

      <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-xl border-b border-border/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/welcome" className="flex items-center gap-2.5 shrink-0">
            <img src={appIcon} alt="" className="w-9 h-9 rounded-xl" />
            <span className="font-extrabold text-lg tracking-tight">SmartPay</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <ThemeToggle />
            {user ? (
              <button
                onClick={() => navigate('/')}
                className="hidden sm:inline-flex px-3 py-2 text-sm font-semibold text-primary hover:underline"
              >
                My home
              </button>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="hidden sm:inline-flex px-3 py-2 text-sm font-semibold text-foreground hover:text-primary"
              >
                Login
              </button>
            )}
            <button
              onClick={() => navigate(primaryHref)}
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:bg-primary/90 shadow-md shadow-primary/20 transition-all"
            >
              <Zap className="w-4 h-4" /> {primaryLabel}
            </button>
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-muted"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/50 lg:hidden"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 26 }}
              className="absolute right-0 top-0 bottom-0 w-80 max-w-[88vw] bg-card p-6 flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-8">
                <span className="font-bold">Menu</span>
                <button onClick={() => setOpen(false)} className="p-2 rounded-lg hover:bg-muted" aria-label="Close menu">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <nav className="space-y-1 flex-1">
                {NAV.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center justify-between px-3 py-3 rounded-xl font-medium ${
                        isActive ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
                      }`
                    }
                  >
                    {item.label} <ChevronRight className="w-4 h-4" />
                  </NavLink>
                ))}
              </nav>
              <div className="space-y-2 pt-4 border-t border-border">
                {!user && (
                  <button
                    onClick={() => { setOpen(false); navigate('/login'); }}
                    className="w-full py-3 border-2 border-primary text-primary rounded-xl font-semibold"
                  >
                    Login
                  </button>
                )}
                <button
                  onClick={() => { setOpen(false); navigate(primaryHref); }}
                  className="w-full py-3 bg-primary text-primary-foreground rounded-xl font-semibold"
                >
                  {primaryLabel}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <main>{children}</main>

      <footer className="bg-[#0F2E2B] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src={appIcon} alt="" className="w-10 h-10 rounded-xl" />
                <span className="font-bold text-xl">SmartPay Utilities</span>
              </div>
              <p className="text-white/70 text-sm leading-relaxed mb-4">
                The friendly way to keep the lights on, the taps running, and your favourite shows playing — from your phone, in seconds.
              </p>
              <a href={`tel:${SUPPORT_TEL}`} className="text-[#99E1D9] text-sm font-medium hover:text-white">
                {SUPPORT_DISPLAY}
              </a>
            </div>
            <div>
              <h4 className="font-bold mb-4">Pay for</h4>
              <ul className="space-y-2 text-white/70 text-sm">
                <li><Link to="/services" className="hover:text-white">Electricity (ESCOM)</Link></li>
                <li><Link to="/services" className="hover:text-white">Water boards</Link></li>
                <li><Link to="/services" className="hover:text-white">DStv &amp; GOtv</Link></li>
                <li><Link to="/services" className="hover:text-white">Tax &amp; more</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Company</h4>
              <ul className="space-y-2 text-white/70 text-sm">
                <li><Link to="/about" className="hover:text-white">About us</Link></li>
                <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
                <li><Link to="/help" className="hover:text-white">Help &amp; FAQ</Link></li>
                <li>
                  <a href={SUPPORT_WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white">
                    WhatsApp support
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Legal</h4>
              <ul className="space-y-2 text-white/70 text-sm">
                <li><Link to="/privacy" className="hover:text-white">Privacy policy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms of service</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row justify-between gap-3 text-sm text-white/50">
            <p>© {new Date().getFullYear()} SmartPay Utilities. Made for households across Malawi.</p>
            <p>Secure payments via PayChangu</p>
          </div>
        </div>
      </footer>

      <a
        href={SUPPORT_WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full bg-emerald-500 text-white shadow-xl flex items-center justify-center hover:scale-105 transition-transform"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="w-7 h-7" />
      </a>
    </div>
  );
}
